import React, {
  useEffect,
  useState,
  useContext,
  useRef,
  useCallback,
} from "react";
import { Helmet } from "react-helmet";
import {
  Autocomplete,
  TextField,
  FormControl,
  MenuItem,
  Select,
  InputLabel,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Skeleton,
  Alert,
  Box,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Typography,
  InputAdornment,
  Grid,
  ListItemText,
  Radio,
  Divider,
  Stack,
  Chip,
  CircularProgress,
} from "@mui/material";
import { Link, useLocation } from "react-router-dom";
import { Icon } from "@iconify/react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { useGetPeoples } from "../../api/settings";
import { BlankPreview } from "../../components/BlankPreview";
import AddMember from "../../components/AddMember";
import SelectCenter from "../../components/SelectCenter.jsx";
import { addMember, updateMember } from "../../api/settings";
import { ANALYTICS_EVENTS, logAnalyticsEvent } from "../../analytics";
import { AuthContext } from "../../context/AuthContextProvider.js";
import { RequireAuth } from "../../RequireAuth.jsx";
import nopreview from "../../assets/images/others/no-preview.png";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import CustomFilters from "../../components/Filters/Filters.jsx";
import useCreditWallet from "../../hooks/useCreditWallet";

const ManagePeoples = ({ onTotalChange }) => {
  const [showMember, setShowMember] = useState(true);
  const [Membertype, setShowMemberType] = useState(true);
  const [selected_member, setSelectedMember] = useState({});
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [hasMore, setHasMore] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [args, setArgs] = useState({ page, limit, isActiveOnly: false });
  const { peoples, isLoading, pagination } = useGetPeoples(args);
  const [allPeoples, setAllPeoples] = useState(() =>
    Array.isArray(peoples) ? peoples : [],
  );
  const [isFirstLoad, setIsFirstLoad] = useState(
    () => !(Array.isArray(peoples) && peoples.length > 0),
  );
  const navigate = useNavigate();
  const location = useLocation();
  const observerRef = useRef();
  const didInitEditRef = useRef(false);
  const { user } = useContext(AuthContext);
  // Same rule as RequireAuth: b2b owner always manages; others need canManageUsers.
  const canManageUsers =
    user?.userType === "b2b" ||
    Object.values(user?.permissions || {}).some((g) => g?.canManageUsers === true);
  // Without the permission, "edit" opens as read-only "view".
  const memberMode = Membertype === "edit" && !canManageUsers ? "view" : Membertype;

  const lastElementRef = useCallback(
    (node) => {
      if (isLoading || isLoadingMore) return;
      if (observerRef.current) observerRef.current.disconnect();
      observerRef.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasMore) {
          loadMoreData();
        }
      });
      if (node) observerRef.current.observe(node);
    },
    [isLoading, isLoadingMore, hasMore],
  );

  const loadMoreData = useCallback(() => {
    if (isLoadingMore || !hasMore) return;
    setIsLoadingMore(true);
    setPage((prevPage) => {
      const nextPage = prevPage + 1;
      setArgs((prevArgs) => ({ ...prevArgs, page: nextPage }));
      return nextPage;
    });
  }, [isLoadingMore, hasMore]);

  useEffect(() => {
    if (peoples && peoples.length > 0) {
      if (page === 1) {
        setAllPeoples(peoples);
        setIsFirstLoad(false);
      } else {
        setAllPeoples((prev) => [...prev, ...peoples]);
      }
      setIsLoadingMore(false);
      const totalPages = Math.ceil((pagination?.total || 0) / limit);
      setHasMore(page < totalPages);
    } else if (peoples && peoples.length === 0 && page > 1) {
      setIsLoadingMore(false);
      setHasMore(false);
    }
  }, [peoples, page, limit, pagination?.total]);

  useEffect(() => {
    if (typeof onTotalChange !== "function") return;
    if (pagination && typeof pagination.total === "number") {
      onTotalChange(pagination.total);
    } else {
      onTotalChange(Array.isArray(allPeoples) ? allPeoples.length : 0);
    }
  }, [pagination?.total, allPeoples, onTotalChange]);

  const handleAddMember = async (data) => {
    if (data == null) {
      closeEdit();
      setArgs({ ...args, reload: Math.random() * 100 });
      return;
    }
    const isUpdate = Boolean(data?.userId);

    try {
      const response = isUpdate
        ? await updateMember(data)
        : await addMember(data);
      if (response.success) {
        // This handler serves both add and edit; only a genuine add is a member_added.
        if (!isUpdate) {
          logAnalyticsEvent(ANALYTICS_EVENTS.MEMBER_ADDED, {
            added_user_id: response?.data?.[0]?._id || response?.data?._id,
            assigned_role: data?.role,
            assigned_centre_id: data?.centerIds?.[0],
          });
        }
        toast.success(response.message);

        const params = new URLSearchParams(window.location.search);
        params.delete("edit");
        const newUrl = `${window.location.pathname}${params.toString() ? `?${params.toString()}` : ""
          }`;
        window.history.replaceState(null, "", newUrl);

        didInitEditRef.current = false;

        setShowMember(true);
        setShowMemberType(true);
        setSelectedMember({});

        // refresh list properly
        setPage(1);
        setAllPeoples([]);
        setHasMore(true);
        setIsFirstLoad(true);

        setArgs((prev) => ({
          ...prev,
          page: 1,
          reload: Date.now(),
        }));
      } else {
        toast.error(response.message);
      }
    } catch (e) {
      toast.error(e.message);
    }
  };

  const handleshowMember = (status) => {
    setShowMember(status);
    setShowMemberType("add");
  };

  // (inside ManagePeoples component)
  const openEdit = useCallback((member) => {
    const params = new URLSearchParams(window.location.search);
    params.set("edit", member._id);

    const newUrl = `${window.location.pathname}?${params.toString()}`;
    window.history.pushState(null, "", newUrl);

    setShowMember(false);
    setShowMemberType("edit");
    setSelectedMember(member);
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const editId = params.get("edit");

    // run only once per edit param
    if (!editId || didInitEditRef.current) return;
    if (allPeoples.length === 0) return;

    const member = allPeoples.find((m) => m._id === editId);

    if (member) {
      setShowMember(false);
      setShowMemberType("edit");
      setSelectedMember(member);
      didInitEditRef.current = true; // prevent rerun on refetch
    }
  }, [allPeoples]);

  const closeEdit = useCallback(() => {
    const params = new URLSearchParams(window.location.search);
    params.delete("edit");

    const newUrl = `${window.location.pathname}${params.toString() ? `?${params.toString()}` : ""
      }`;
    window.history.replaceState(null, "", newUrl);

    didInitEditRef.current = false;

    setShowMember(true);
    setShowMemberType(true);
    setSelectedMember({});

    setPage(1);
    setAllPeoples([]);
    setHasMore(true);
    setIsFirstLoad(true);

    setArgs((prev) => ({
      ...prev,
      page: 1,
      reload: Date.now(),
    }));
  }, []);
  // keep component state in sync with URL hash (handles Back/Forward)
  useEffect(() => {
    const applyHash = () => {
      const isEditHash = window.location.hash === "#edit";

      if (isEditHash) {
        // If hash is #edit and we don't already have edit open, open it.
        // Note: If you want to persist which member was being edited across refresh,
        // you'd need to store selected_member info in sessionStorage / query param.
        setShowMember(false);
        setShowMemberType("edit");
      } else {
        // no hash → list view
        setShowMember(true);
        setShowMemberType(true);
        setSelectedMember({});
      }
    };

    // initial sync (handles page reload with #edit)
    applyHash();
    window.addEventListener("hashchange", applyHash);
    return () => window.removeEventListener("hashchange", applyHash);
  }, []);

  const handleFilter = (filters) => {
    setAllPeoples([]);
    setHasMore(true);
    setIsFirstLoad(true);
    setPage(1);
    setArgs({ ...filters, page: 1, limit });
  };

  return (
    <>
      <Helmet>
        <title>Spandan - Manage Members</title>
      </Helmet>
      {showMember ? (
        <Box display="flex" flexWrap="wrap">
          <Box width="100%">
            <Filter
              handleshowMember={handleshowMember}
              handleFilter={handleFilter}
              Membertype={Membertype}
            />

            <CustomFilters
              component="members"
              onApply={(filters) => {
                setPage(1);
                setAllPeoples([]);
                setHasMore(true);
                setIsFirstLoad(true);

                setArgs({
                  page: 1,
                  limit,
                  sortBy: filters.sort,
                  searchText: filters.searchText || "",

                  ...(filters.roles && filters.roles.length > 0 && {
                    role: filters.roles.join(","),
                  }),

                  ...(filters.status && filters.status.length > 0 && {
                    status: filters.status.join(","),
                  }),

                  ...(filters.spokeIds?.length > 0 && {
                    centerIds: filters.spokeIds.join(","),
                    centerType: "spoke",
                  }),

                  ...(filters.hubIds?.length > 0 &&
                    filters.spokeIds?.length === 0 && {
                    centerIds: filters.hubIds.join(","),
                    centerType: "hub",
                  }),
                });
              }}
            />

            {isLoading && isFirstLoad ? (
              <Box className="mt-3">
                <TableContainer className="app-table-card"
                  sx={{
                    mt: 0,
                    border: "none",
                    boxShadow: "none",
                    overflowX: "auto",
                    maxHeight: "70vh",     // IMPORTANT
                    overflowY: "auto",
                  }}
                >
                  <Table
                    stickyHeader
                    sx={{
                      minWidth: 700,
                      backgroundColor: "var(--app-surface-2)",
                      borderCollapse: "separate",
                      borderSpacing: "0 10px",
                      "& th": {
                        borderBottom: "none",
                        fontWeight: 800,
                        fontSize: "13px",
                        color: "#A3A3A3",
                        py: 1.5,
                      },
                      "& td": {
                        borderBottom: "none",
                        fontSize: { xs: "13px", md: "15px" },
                        py: { xs: 1.2, md: 1.5 },
                        px: { xs: 1.2, md: 2 },
                        color: "var(--app-text)",
                      },
                    }}
                  >
                    {/* SAME HEADERS AS FINAL TABLE */}
                    <TableHead>
                      <TableRow>
                        {[
                          "NAME",
                          "USER ID",
                          "MOBILE NUMBER",
                          "EMAIL ADDRESS",
                          "ROLE",
                          "DESIGNATION",
                        ].map((head) => (
                          <TableCell
                            key={head}
                            align="left"
                            sx={{ backgroundColor: "var(--app-surface-2)" }}
                          >
                            <Typography
                              sx={{
                                fontSize: { xs: "11px", md: "15px" },
                                fontWeight: 800,
                                fontFamily: "Figtree",
                              }}
                            >
                              {head}
                            </Typography>
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>

                    {/* SKELETON ROWS WITH SAME CELL COUNT */}
                    <TableBody>
                      {Array.from({ length: 5 }).map((_, rowIndex) => (
                        <TableRow
                          key={rowIndex}
                          sx={{
                            backgroundColor: "var(--app-surface)",
                            "& > *:first-of-type": {
                              borderTopLeftRadius: "8px",
                              borderBottomLeftRadius: "8px",
                            },
                            "& > *:last-of-type": {
                              borderTopRightRadius: "8px",
                              borderBottomRightRadius: "8px",
                            },
                          }}
                        >
                          {Array.from({ length: 6 }).map((__, colIndex) => (
                            <TableCell key={colIndex}>
                              <Skeleton
                                variant="text"
                                width="100%"
                                height={28}
                              />
                            </TableCell>
                          ))}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            ) : allPeoples.length > 0 ? (
              <>
                <TableContainer className="app-table-card"
                  sx={{
                    mt: 2,
                    border: "none",
                    boxShadow: "none",
                    overflowX: "auto", // mobile scroll
                    maxHeight: "70vh",     // IMPORTANT
                    overflowY: "auto",
                  }}
                >
                  <Table
                    stickyHeader
                    sx={{
                      minWidth: 700,
                      backgroundColor: "var(--app-surface-2)",
                      borderCollapse: "separate",
                      borderSpacing: "0 10px",
                      "& th": {
                        borderBottom: "none",
                        fontWeight: 800,
                        fontSize: "13px",
                        color: "#A3A3A3",
                        py: 1.5,
                        fontFamily: "Figtree",
                      },
                      "& td": {
                        borderBottom: "none",
                        fontSize: { xs: "13px", md: "15px" },
                        py: { xs: 1.2, md: 1.5 },
                        px: { xs: 1.2, md: 2 },
                        color: "var(--app-text)",
                        fontFamily: "Figtree",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      },
                    }}
                  >
                    <TableHead>
                      <TableRow>
                        {[
                          "NAME",
                          "USER ID",
                          "MOBILE NUMBER",
                          "EMAIL ADDRESS",
                          "ROLE",
                          "DESIGNATION",
                        ].map((head) => (
                          <TableCell
                            key={head}
                            align="left"
                            sx={{ backgroundColor: "var(--app-surface-2)" }}
                          >
                            <Typography
                              sx={{
                                fontSize: { xs: "11px", md: "15px" },
                                fontWeight: 800,
                                fontFamily: "Figtree",
                              }}
                            >
                              {head}
                            </Typography>
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>

                    <TableBody>
                      {allPeoples.map((member, index) => (
                        <React.Fragment key={index}>
                          <TableRow
                            sx={{
                              backgroundColor: "var(--app-surface)",
                              borderRadius: "8px",
                              "& > *:first-of-type": {
                                borderTopLeftRadius: "8px",
                                borderBottomLeftRadius: "8px",
                              },
                              "& > *:last-of-type": {
                                borderTopRightRadius: "8px",
                                borderBottomRightRadius: "8px",
                              },
                              fontFamily: "Figtree",
                            }}
                          >
                            {/* NAME */}
                            <TableCell sx={{ width: "22%" }}>
                              <Box display="flex" alignItems="center" gap={1}>
                                <Avatar
                                  sx={{
                                    width: { xs: 26, md: 30 },
                                    height: { xs: 26, md: 30 },
                                    backgroundColor: "var(--app-border)",
                                    color: "#A3A3A3",
                                    fontSize: { xs: "13px", md: "13px" },
                                    fontWeight: 700,
                                  }}
                                  src={member?.profileImage || ""}
                                >
                                  {!member?.profileImage &&
                                    (member?.fullName
                                      ? member.fullName
                                        .split(" ")
                                        .map((n) => n[0])
                                        .join("")
                                        .substring(0, 2)
                                        .toUpperCase()
                                      : "N")}
                                </Avatar>
                                <Typography
                                  sx={{
                                    textDecoration: "underline",
                                    cursor: "pointer",
                                    whiteSpace: "nowrap",
                                    fontSize: { xs: "13px", md: "15px" },
                                  }}
                                  onClick={() => openEdit(member)}
                                >
                                  {member.fullName || "--"}
                                </Typography>
                              </Box>
                            </TableCell>

                            {/* USER ID (displays virtualId, labeled "User ID" in the UI) */}
                            <TableCell sx={{ width: "15%" }}>
                              {member?.virtualId || "--"}
                            </TableCell>

                            {/* MOBILE */}
                            <TableCell sx={{ width: "18%" }}>
                              {member?.phoneNo
                                ? `${member?.countryCode ?? ""} ${member?.phoneNo}`
                                : "-"}
                            </TableCell>

                            {/* EMAIL */}
                            <TableCell sx={{ wordBreak: "break-word", width: "25%" }}>
                              {member?.email || "--"}
                            </TableCell>

                            {/* ROLE */}
                            <TableCell sx={{ width: "15%" }}>
                              {member?.displayRole || "--"}
                            </TableCell>

                            {/* DESIGNATION */}
                            <TableCell sx={{ width: "20%" }}>
                              <Stack
                                direction="row"
                                alignItems="center"
                                justifyContent="space-between"
                                sx={{ width: "80%" }}
                              >
                                <Typography noWrap>
                                  {member?.designation || "--"}
                                </Typography>

                                {member?.userStatus === "invited" && (
                                  <Chip
                                    label="Invited"
                                    sx={{
                                      width: "65px",
                                      height: "24px",
                                      borderRadius: "4px",
                                      px: "6px",
                                      py: "4px",
                                      backgroundColor: "#FFEACC",
                                      color: "#C77800",
                                      display: "inline-flex",
                                      alignItems: "center",
                                      justifyContent: "center",

                                      "& .MuiChip-label": {
                                        p: 0,
                                        fontFamily: "Figtree",
                                        fontWeight: 700,
                                        fontSize: "12px",
                                        lineHeight: "100%",
                                        letterSpacing: "0.05em",
                                        textAlign: "center",
                                        color: "#C77800",
                                      },
                                    }}
                                  />
                                )}
                              </Stack>
                            </TableCell>
                          </TableRow>

                          {member?.userStatus === "invited" &&
                            allPeoples[index + 1]?.userStatus !== "invited" && (
                              <TableRow>
                                <TableCell colSpan={5} sx={{ p: 0 }}>
                                  <Divider
                                    sx={{
                                      border: "1px solid  #E3E3E3",
                                      m: 0,
                                      backgroundColor: "none",
                                    }}
                                  />
                                </TableCell>
                              </TableRow>
                            )}
                        </React.Fragment>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>

                {/* LOADER / END MESSAGE */}
                <Box ref={lastElementRef} textAlign="center" py={2}>
                  {(isLoadingMore || isLoading) && (
                    <Box
                      display="flex"
                      justifyContent="center"
                      alignItems="center"
                      gap={1}
                    >
                      <CircularProgress size={20} />
                      <Typography variant="caption">
                        Loading more...
                      </Typography>
                    </Box>
                  )}

                  {!hasMore && !isLoadingMore && allPeoples.length > 0 && (
                    <Typography variant="caption" color="text.secondary">
                      {" "}
                    </Typography>
                  )}
                </Box>
              </>
            ) : (
              <Box sx={{ paddingTop: 2 }}>
                <BlankPreview icon={nopreview} title="No Member Available" />
              </Box>
            )}
          </Box>
        </Box>
      ) : (
        <Box display="flex" flexWrap="wrap">
          <Box width="100%">
            <h5
              style={{ cursor: "pointer", marginBottom: 20 }}
              onClick={closeEdit}
            >
              <strong>
                <i className="fas fa-angle-left"></i>{" "}
                {memberMode === "add"
                  ? "Add New Member"
                  : memberMode === "view"
                    ? "View Member"
                    : "Edit Member"}
              </strong>
            </h5>
            <AddMember
              handleAddMember={handleAddMember}
              userId={selected_member.user_id}
              type={memberMode}
            />
          </Box>
        </Box>
      )}
    </>
  );
};

function Filter({ handleshowMember, handleFilter, Membertype }) {
  const navigate = useNavigate();
  const { all_centres } = useContext(AuthContext);
  const [modelFilter, setModelFilter] = useState(false);
  const [selectedHubIds, setSelectedHubIds] = useState([]);
  const [selectedRoles, setSelectedRoles] = useState([]);
  const [selectedStatuses, setSelectedStatuses] = useState([]);
  const [sortDirection, setSortDirection] = useState("desc");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedSpokeIds, setSelectedSpokeIds] = useState([]);
  const { isUserLimitReached } = useCreditWallet();

  const getEffectiveCenterIds = () => {
    if (selectedSpokeIds.length > 0) return selectedSpokeIds;
    if (selectedHubIds.length > 0) return selectedHubIds;
    return [];
  };
  const ROLE_MAP = {
    admin: "b2b-admin",
    member: "b2b-member",
    doctor: "b2b-doctor",
    viewer: "b2b-viewer",
  };

  const applyFilters = useCallback(
    (override = {}) => {
      const centerIdsArr = getEffectiveCenterIds();

      handleFilter({
        page: 1,
        sortBy: sortDirection, // FIX
        role: selectedRoles.map((r) => ROLE_MAP[r]).join(","),
        status: selectedStatuses.join(","),
        searchText: searchTerm,

        ...(centerIdsArr.length > 0 && {
          centerIds: centerIdsArr.join(","),
          centerType: selectedSpokeIds.length > 0 ? "spoke" : "hub",
        }),

        ...override,
      });
    },
    [
      sortDirection,
      selectedRoles,
      selectedStatuses,
      searchTerm,
      selectedHubIds,
      selectedSpokeIds,
      handleFilter,
    ],
  );

  useEffect(() => {
    applyFilters({
      searchText: searchTerm || "",
      page: 1,
    });
  }, [searchTerm]);

  const handleFilterChange = (newFilters) => {
    handleFilter({
      sortBy: sortDirection,
      role: selectedRoles.map((r) => ROLE_MAP[r]).join(","),
      status: selectedStatuses.join(","),
      centerIds: getEffectiveCenterIds(),
      searchText: searchTerm,
      ...newFilters,
    });
  };

  const DropdownIcon = (props) => (
    <Icon
      icon="eva:arrow-ios-downward-fill"
      width="20"
      height="20"
      {...props} // ← IMPORTANT
    />
  );

  return (
    <>
      <Box>
        <Grid
          container
          spacing={2}
          alignItems="center"
          sx={{
            mt: 1,
            flexWrap: { xs: "wrap", md: "nowrap" },
          }}
        >
          {/* Search Input */}
          <TextField
            variant="outlined"
            placeholder="Search by Name , Mobile Number"
            value={searchTerm}
            onChange={(e) => {
              const value = e.target.value;
              setSearchTerm(value);
            }}
            fullWidth
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Icon icon="gg:search" width="14" height="14" color="var(--app-text-muted)" />
                </InputAdornment>
              ),
              sx: {
                borderRadius: "8px",
                border: "1px solid var(--app-border)",
                backgroundColor: "var(--app-surface)",
                height: "45px",
                fontSize: "14px",
                "& fieldset": { border: "none" },
              },
            }}
          />

          <RequireAuth
            role={["b2b", "b2b-admin", "b2b-member"]}
            permission={"canManageUsers"}
          >
            <Button
              component={Link}
              to="/add-member"
              variant="contained"
              startIcon={<AddIcon />}
              onClick={(e) => {
                if (isUserLimitReached) {
                  e.preventDefault();
                  toast.error(
                    "You've reached your plan's member limit. Upgrade your plan to add more members."
                  );
                }
              }}
              sx={{
                backgroundColor: "#5751E9",
                boxShadow: "none",
                fontSize: "14px",
                fontWeight: 600,
                textTransform: "none",
                height: "45px",
                borderRadius: "10px",
                whiteSpace: "nowrap",
                fontFamily: "figtree",
                px: 5,
                "&:hover": {
                  backgroundColor: "#433AC7",
                  color: "white",
                },
              }}
            >
              Add New Member
            </Button>
          </RequireAuth>
        </Grid>
      </Box>


    </>
  );
}

export default ManagePeoples;